import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { IIconSource } from './icon-source';

/**
 * Provides icons via HTTP requests at runtime.
 * Uses in-memory caching to avoid redundant network requests.
 */
@Injectable({
  providedIn: 'root'
})
export class HttpIconSource extends IIconSource {
  private readonly cache = new Map<string, string>();
  private readonly basePath = 'assets/icons';

  constructor(private readonly http: HttpClient) {
    super();
  }

  /**
   * Synchronously retrieves an icon from cache.
   * Returns null if not cached - use fetchIcon() for async loading.
   */
  getIcon(name: string): string | null {
    const path = this.buildPath(name);
    return this.cache.get(path) ?? null;
  }

  /**
   * Checks if icon is currently in cache.
   */
  hasIcon(name: string): boolean {
    const path = this.buildPath(name);
    return this.cache.has(path);
  }

  /**
   * Fetches an icon asynchronously via HTTP.
   * Caches the result for future synchronous access.
   * @param name - Icon name without extension
   * @returns Observable of SVG content, or null on error
   */
  fetchIcon(name: string): Observable<string | null> {
    const path = this.buildPath(name);

    // Return cached version if available
    if (this.cache.has(path)) {
      return of(this.cache.get(path)!);
    }

    return this.http.get(path, { responseType: 'text' }).pipe(
      tap((svg) => this.cache.set(path, svg)),
      catchError((error) => {
        console.error(`Failed to load icon: ${name}`, error);
        return of(null);
      })
    );
  }

  private buildPath(name: string): string {
    return `${this.basePath}/${name}.svg`;
  }
}
